import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import type { SegmentRule, VoterCounts, VoterDataFieldDto, VoterDataOptionDto } from '@signalvote/shared';
import { assertNever } from '@signalvote/shared';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { formatCount } from './VoterDataFieldPopover';
import { searchFields } from './voterFieldSearch';
import { fieldCountsOf, isNarrowed, statusOf } from './voterSegment';

/** Requests the empty blank suggests. Only those that find something in the catalog are shown. */
const EXAMPLE_REQUESTS = ['teachers', 'women', 'gun owners', 'seniors'];

/** A plain-English request the empty blank suggests, when requests can be described. */
const EXAMPLE_DESCRIPTION = 'women over 50 who aren’t Republicans';

/** Typed text this many words long reads as a sentence, so describing it is the default choice. */
const SENTENCE_WORDS = 3;

type Choice =
  | { kind: 'example'; text: string }
  | { kind: 'field'; field: VoterDataFieldDto }
  | { kind: 'value'; field: VoterDataFieldDto; option: VoterDataOptionDto }
  | { kind: 'describe'; text: string };

/** A labelled run of choices: a matched field and its values, or a group of the empty blank. */
interface Section {
  key: string;
  label: string;
  heading?: string;
  choices: Choice[];
}

/**
 * The sentence's last blank: one input that finds fields and values. Empty, it suggests example
 * requests and the catalog's most-used fields, so it's never a blank prompt. Typing searches
 * labels, categories, synonyms and values; a value applies directly, a field opens its popover.
 * A combobox: arrows move, Enter chooses, Escape closes and then clears.
 *
 * It also takes plain English: whatever was typed can be described ("women over 50 who aren't
 * Republicans"), the last choice in the list. For a word or two the best field match stays the
 * default; when nothing matches, or the text reads as a sentence, describing it is.
 *
 * The one place a customer adds to the list.
 */
export function VoterDataFieldSearch({
  fields,
  rules,
  counts,
  placeholder,
  capReason = null,
  onChooseField,
  onChooseValue,
  onDescribe,
}: {
  fields: VoterDataFieldDto[];
  rules: SegmentRule[];
  counts: VoterCounts | undefined;
  placeholder: string;
  /** Why no rule can be added, at L2's filter cap; fields already in the list can still be edited. */
  capReason?: string | null;
  onChooseField: (field: VoterDataFieldDto) => void;
  onChooseValue: (field: VoterDataFieldDto, option: VoterDataOptionDto) => void;
  /** Applies a plain-English request, calling `onApplied` once it has. Without it, the blank only searches. */
  onDescribe?: (text: string, onApplied: () => void) => void;
}) {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [active, setActive] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const optionId = (i: number) => `${listId}-${i}`;

  const text = query.trim();
  const groups = searchFields(fields, query);
  const examples = useMemo(() => {
    const found = EXAMPLE_REQUESTS.filter((example) => searchFields(fields, example).length > 0);
    return onDescribe ? [...found, EXAMPLE_DESCRIPTION] : found;
  }, [fields, onDescribe]);
  const describeSection: Section[] =
    text && onDescribe ? [{ key: 'describe', label: 'Describe', heading: 'Describe', choices: [{ kind: 'describe', text }] }] : [];
  const sections: Section[] = text
    ? [
        ...groups.map((g) => ({
          key: g.field.id,
          label: g.field.label,
          choices: [{ kind: 'field' as const, field: g.field }, ...g.values.map((option) => ({ kind: 'value' as const, field: g.field, option }))],
        })),
        ...describeSection,
      ]
    : [
        { key: 'try', label: 'Try', heading: 'Try', choices: examples.map((text) => ({ kind: 'example' as const, text })) },
        { key: 'common', label: 'Most used', heading: 'Most used', choices: fields.filter((f) => f.common).map((field) => ({ kind: 'field' as const, field })) },
      ].filter((section) => section.choices.length > 0);
  // Keyboard order and render order are the same list.
  const choices = sections.flatMap((section) => section.choices);

  // The best match is ready for Enter: the top field's value the text names, or the field itself —
  // or describing what was typed, when nothing matches or it reads as a sentence. An empty blank
  // waits for an arrow key.
  const describeFirst = describeSection.length > 0 && (groups.length === 0 || text.split(/\s+/).length >= SENTENCE_WORDS);
  const pick = groups[0]?.pick;
  const defaultActive = !text ? -1 : describeFirst ? choices.length - 1 : pick ? 1 + groups[0].values.indexOf(pick) : 0;
  useEffect(() => setActive(defaultActive), [query, defaultActive]);
  useEffect(() => {
    if (expanded && active >= 0) document.getElementById(optionId(active))?.scrollIntoView?.({ block: 'nearest' });
    // optionId is derived from listId, which never changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, active]);

  const blocked = (choice: Choice) =>
    capReason !== null && (choice.kind === 'field' || choice.kind === 'value') && !isNarrowed(choice.field, rules);

  const choose = (choice: Choice) => {
    if (blocked(choice)) return;
    switch (choice.kind) {
      case 'example':
        setQuery(choice.text);
        return;
      case 'field':
        onChooseField(choice.field);
        break;
      case 'value':
        onChooseValue(choice.field, choice.option);
        break;
      case 'describe':
        onDescribe?.(choice.text, () => setQuery(''));
        setExpanded(false);
        return;
      default:
        assertNever(choice);
    }
    setQuery('');
    setExpanded(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        e.preventDefault();
        if (!expanded) return setExpanded(true);
        if (choices.length === 0) return;
        const step = e.key === 'ArrowDown' ? 1 : -1;
        setActive((i) => (i < 0 ? (step > 0 ? 0 : choices.length - 1) : (i + step + choices.length) % choices.length));
        return;
      }
      case 'Enter':
        if (expanded && choices[active]) {
          e.preventDefault();
          choose(choices[active]);
        }
        return;
      case 'Escape':
        e.preventDefault();
        if (expanded) setExpanded(false);
        else setQuery('');
        return;
    }
  };

  const renderChoice = (choice: Choice, i: number) => {
    const { label, content, className } = describe(choice, rules, counts);
    return (
      <div
        key={optionId(i)}
        id={optionId(i)}
        role="option"
        aria-selected={i === active}
        aria-disabled={blocked(choice) || undefined}
        aria-label={label}
        // Keep focus in the blank, so choosing doesn't blur it.
        onMouseDown={(e) => e.preventDefault()}
        onMouseMove={() => setActive(i)}
        onClick={() => choose(choice)}
        className={cn(
          'flex min-h-9 cursor-pointer items-center gap-3 rounded-md px-2.5 py-1.5 text-sm aria-disabled:cursor-not-allowed aria-disabled:opacity-50',
          i === active && 'bg-accent text-accent-foreground',
          className,
        )}
      >
        {content}
      </div>
    );
  };

  return (
    <Popover open={expanded} onOpenChange={setExpanded}>
      <PopoverAnchor asChild>
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-label="Add who’s in the list"
          aria-expanded={expanded}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={expanded && active >= 0 ? optionId(active) : undefined}
          autoComplete="off"
          spellCheck={false}
          value={query}
          placeholder={placeholder}
          onChange={(e) => {
            setQuery(e.target.value);
            setExpanded(true);
          }}
          onFocus={() => setExpanded(true)}
          onBlur={() => setExpanded(false)}
          onKeyDown={onKeyDown}
          style={{ width: `${Math.max(query.length, placeholder.length) + 1}ch` }}
          className="ml-[0.3em] max-w-full rounded-md border-b-2 border-dashed border-primary/40 bg-transparent px-1 font-semibold text-foreground placeholder:font-medium placeholder:text-muted-foreground/70 focus:border-solid focus:border-primary focus:outline-none"
        />
      </PopoverAnchor>
      <PopoverContent
        id={listId}
        role="listbox"
        aria-label="Fields and values"
        align="start"
        className="max-h-[min(420px,60vh)] w-[min(400px,calc(100vw-24px))] overflow-y-auto p-1.5 motion-reduce:animate-none"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          if (e.target === inputRef.current) e.preventDefault();
        }}
      >
        {capReason && <p className="px-2.5 py-2 text-sm text-muted-foreground">{capReason}</p>}
        {text && groups.length === 0 && (
          <p role="status" className="px-2.5 py-2 text-sm text-muted-foreground">
            No fields match “{text}”.
          </p>
        )}
        {sections.map((section) => {
            const offset = choices.indexOf(section.choices[0]);
            return (
              <div key={section.key} role="group" aria-label={section.label} className={cn(!section.heading && 'border-t py-1 first:border-t-0')}>
                {section.heading && (
                  <p className="px-2.5 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">{section.heading}</p>
                )}
                {section.choices.map((choice, j) => renderChoice(choice, offset + j))}
              </div>
            );
          })}
      </PopoverContent>
    </Popover>
  );
}

/** How a choice reads in the list: its accessible name and what it shows. */
function describe(choice: Choice, rules: SegmentRule[], counts: VoterCounts | undefined): { label: string; content: ReactNode; className?: string } {
  switch (choice.kind) {
    case 'example':
      return { label: choice.text, content: <span className="italic text-foreground/80">{choice.text}</span> };
    case 'field': {
      const { field } = choice;
      const status = statusOf(field, rules);
      return {
        label: `${field.label}, ${status}`,
        content: (
          <>
            <span className="flex-1 font-semibold">
              {field.label} <span className="font-normal text-muted-foreground">{field.category}</span>
            </span>
            <span className={cn('text-xs', isNarrowed(field, rules) ? 'font-semibold text-primary' : 'text-muted-foreground')}>{status}</span>
          </>
        ),
      };
    }
    case 'describe':
      return {
        label: `Describe “${choice.text}”`,
        content: (
          <span className="flex-1">
            <span className="font-semibold">Describe</span> <span className="text-foreground/80">“{choice.text}”</span>
          </span>
        ),
      };
    case 'value':
      return {
        label: `${choice.option.label}, ${choice.field.label}`,
        className: 'pl-6',
        content: (
          <>
            <span className="flex-1">{choice.option.label}</span>
            <span className="text-xs tabular-nums text-muted-foreground">{formatCount(fieldCountsOf(choice.field, rules, counts, undefined).options[choice.option.label])}</span>
          </>
        ),
      };
  }
}
